import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { Button, Space, Row, Spinner } from "@masterteam/mt-design-react";

import Table from "../NewTable";
import DeleteModal from "../DeleteModal";
import UpdateValueForm from "./UpdateValueForm";
import PerformanceIndicatorForm from "../AddNewInitiative/PerformanceIndicators/PerformanceIndicatorForm";

import api from "../../providers/api";
import useIsAr from "../../Hooks/useIsAr";
import { addToast, UnitOfMeasureForInitiative } from "../../helpers";

import { ReactComponent as EditIcon } from "../../assets/images/edit.svg";
import { ReactComponent as DeleteIcon } from "../../assets/images/delete.svg";
import { ReactComponent as RefreshIcon } from "../../assets/images/refresh-icon.svg";

import "./style.scss";

const initialModalState = {
  isOpen: false,
  data: null,
};

const InitiativeKPIs = ({ data }) => {
  const intl = useIntl();
  const [isAr] = useIsAr();

  const [KPIs, setKPIs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteModal, setDeleteModal] = useState(initialModalState);
  const [formModal, setFormModal] = useState(initialModalState);
  const [updateModal, setUpdateModal] = useState(initialModalState);
  const [currentUser] = useSelector(({ user }) => [user.user]);

  const isReadOnly = currentUser?.UserName !== data?.ManagerModel?.UserName;

  const getInitiativeKPIs = async () => {
    setLoading(true);
    try {
      const res = await api.initiatives.getKPIsById(data.ID);
      setKPIs(res.Data);
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id) => {
    setDeleteLoading(true);
    api.initiatives
      .deleteKPI(id)
      .then((res) => {
        const { StatusCode, StatusMessage } = res;
        if (StatusCode === "Success") {
          getInitiativeKPIs();
          setDeleteModal(initialModalState);
          addToast.success("toast.successDelete");
        } else if (StatusCode === "fail") {
          addToast.error(StatusMessage, {
            toastId: "get-users-error",
          });
        }
      })
      .catch((error) => {
        addToast.error(error.message, {
          toastId: "get-users-error",
        });
      })
      .finally(() => {
        setDeleteLoading(false);
      });
  };

  useEffect(() => {
    getInitiativeKPIs();
  }, []);

  const columns = [
    {
      key: "Code",
      cell: (item) => item.Code,
      header: intl.formatMessage({ id: "common.code" }),
      width: "10%",
    },
    {
      key: "Name",
      cell: (item) => (isAr ? item?.ArabicName : item?.EnglishName),
      header: intl.formatMessage({
        id: "initiative.performance-indicator-name",
      }),
      width: "30%",
    },
    {
      key: "Description",
      cell: (item) =>
        isAr ? item?.ArabicDescription : item?.EnglishDescription,
      header: intl.formatMessage({
        id: "initiative.performance-indicator-description",
      }),
      width: "30%",
    },
    {
      key: "UnitOfMeasure",
      cell: (item) => {
        const unitOfMeasure = UnitOfMeasureForInitiative.find(
          (e) => e.value === item?.UnitOfMeasure
        );

        return isAr ? unitOfMeasure?.labelAr : unitOfMeasure?.label;
      },
      header: intl.formatMessage({ id: "kpi.unit-of-measure" }),
      width: "15%",
    },
    {
      key: "Value",
      cell: (item) =>
        item?.UnitOfMeasure === "MultipleChoice"
          ? item?.BooleanValue
            ? intl.formatMessage({ id: "common.yes" })
            : item?.BooleanValue === false
            ? intl.formatMessage({ id: "common.no" })
            : "---"
          : `${item?.Value} ${item?.UnitOfMeasure === "Percentage" ? "%" : ""}`,
      header: intl.formatMessage({ id: "common.value" }),
      width: "15%",
    },
  ];

  const actions = [
    {
      key: "update",
      variant: "primary",
      Icon: RefreshIcon,
      "tooltip-position": "buttom",
      tooltip: intl.formatMessage({ id: "common.update" }),
      onClick: (item) => {
        setUpdateModal({ isOpen: true, data: item });
      },
    },
    {
      key: "edit",
      variant: "primary",
      Icon: EditIcon,
      "tooltip-position": "buttom",
      tooltip: intl.formatMessage({ id: "common.edit" }),
      onClick: (item) => {
        setFormModal({ isOpen: true, data: item });
      },
    },
    {
      key: "delete",
      variant: "error",
      Icon: DeleteIcon,
      "tooltip-position": "buttom",
      tooltip: intl.formatMessage({ id: "common.delete" }),
      onClick: (item) => {
        setDeleteModal({ isOpen: true, data: item.ID });
      },
    },
  ];

  return (
    <Space className="initiative-kpis" direction="vertical">
      <Row align="center" justify="between">
        <div className="title-wrapper">
          {intl.formatMessage({
            id: "initiative.initiative-performance-indicators",
          })}
        </div>

        {!isReadOnly && (
          <Button
            shape="round"
            onClick={() => setFormModal({ isOpen: true, data: null })}
          >
            {intl.formatMessage({ id: "common.add" })}
          </Button>
        )}
      </Row>

      {loading ? (
        <Row justify="center">
          <Spinner size="large" />
        </Row>
      ) : (
        <Table
          data={KPIs}
          columns={columns}
          actions={isReadOnly ? [] : actions}
          headerHasBackgroundColor
        />
      )}

      {formModal.isOpen && (
        <PerformanceIndicatorForm
          data={formModal.data}
          performanceIndicators={KPIs}
          setPerformanceIndicators={setKPIs}
          closeModal={() => setFormModal(initialModalState)}
          initiativeData={data}
          getData={getInitiativeKPIs}
        />
      )}

      {updateModal.isOpen && (
        <UpdateValueForm
          data={updateModal.data}
          performanceIndicators={KPIs}
          setPerformanceIndicators={setKPIs}
          closeModal={() => setUpdateModal(initialModalState)}
          initiativeData={data}
          getData={getInitiativeKPIs}
        />
      )}

      {deleteModal.isOpen ? (
        <DeleteModal
          isOpen={deleteModal.isOpen}
          title={intl.formatMessage({
            id: "initiative.initiative-performance-indicator",
          })}
          closeModal={() => setDeleteModal(initialModalState)}
          handleDelete={() => handleDelete(deleteModal.data)}
          ButtonStatus={deleteLoading}
        />
      ) : null}
    </Space>
  );
};

export default InitiativeKPIs;
